import { DownOutlined } from '@ant-design/icons';
import { Image2FilledIcon } from '@marine/icons';
import { Avatar, Button, Dropdown, Menu, Space, Spin } from 'antd';
import React, { FC, Suspense, useCallback, useMemo, useState } from 'react';
import { FileCard, useFilesUrlWithToken } from 'services';
import styles from './ProfilePictureInput.module.scss';

const FilePicker = React.lazy(() => import('../FilePicker'));

interface ProfilePictureInputProps {
  value?: any;
  onChange?: (value?: any) => void;
  firstName?: string | null;
  lastName?: string | null;
  width?: number;
  height?: number;
  quality?: number;
  format?: 'card' | 'card-lg' | 'card-sm' | 'thumbnail' | 'thumbnail-lg' | 'thumbnail-sm' | 'cover' | 'cover-sm';
}

const ProfilePictureInput: FC<ProfilePictureInputProps> = ({
  value,
  firstName,
  lastName,
  onChange = () => {},
  width,
  height,
  quality,
  format,
}) => {
  const [filePickerVisible, setFilePickerVisible] = useState(false);

  const initials = useMemo(() => {
    let _initials = '';

    if (firstName?.length) {
      _initials += firstName?.substring(0, 1).toLocaleUpperCase();
    }
    if (lastName?.length) {
      _initials += lastName?.substring(0, 1).toLocaleUpperCase();
    }

    return _initials;
  }, [firstName, lastName]);

  const { fileUrlWithCroppingParamAndToken } = useFilesUrlWithToken();

  const imageUrl = useMemo(() => {
    if (value?.id) {
      return fileUrlWithCroppingParamAndToken(value?.url, { width, height, quality, format });
    }

    return null;
  }, [fileUrlWithCroppingParamAndToken, value?.id, value?.url, width, height, quality, format]);

  const handleRemoveCover = useCallback(() => {
    onChange(null);
  }, [onChange]);

  const initialValue = useMemo(() => (value ? { ...value, type: 'image' } : undefined), [value]);

  const handleChangeProfilePicture = useCallback(() => {
    setFilePickerVisible(true);
  }, []);

  const handleOnOkay = (items: FileCard[]) => {
    onChange(items[0]);
  };

  return (
    <div className={styles.wrapper}>
      <h4>Profile picture</h4>
      <Space>
        <Avatar src={imageUrl} className={styles.avatar} size={80} shape="circle">
          {initials}
        </Avatar>
        {imageUrl?.length ? (
          <Dropdown.Button
            icon={<DownOutlined />}
            trigger={['click']}
            dropdownRender={() => (
              <Menu
                items={[
                  {
                    key: 'remove-cover',
                    label: 'Remove profile picture',
                    onClick: handleRemoveCover,
                    style: { height: '22px' },
                    danger: true,
                  },
                ]}
              />
            )}
            onClick={handleChangeProfilePicture}
          >
            <Image2FilledIcon /> Change picture
          </Dropdown.Button>
        ) : (
          <Button onClick={handleChangeProfilePicture} icon={<Image2FilledIcon />}>
            Set profile picture
          </Button>
        )}
      </Space>
      {filePickerVisible && (
        <Suspense fallback={<Spin spinning fullscreen />}>
          <FilePicker
            title="Change profile picture"
            initialSelectedFiles={initialValue ? [initialValue] : []}
            close={() => setFilePickerVisible(false)}
            onFinish={handleOnOkay}
            open={filePickerVisible}
            accepted="image"
          />
        </Suspense>
      )}
    </div>
  );
};

export { ProfilePictureInput };
